import clsx from 'clsx';
import {useCallback, useMemo, useRef} from 'react';

import AemImage from '@/components/AemImage/AemImage';
import BackButton from '@/components/BackButton/BackButton';
import {NAVIGATION_DIRECTION} from '@/navigation/navigationDirection';
import {PageTransition} from '@/navigation/PageTransition';
import {usePageTransition} from '@/navigation/usePageTransition';
import type {TipsItem} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';
import {TIPS_CARD_SIZES} from '@/types/content';
import TipsCard from './components/TipsCard';
import {useInitialCardNavigation} from './hooks/useInitialCardNavigation';
import useTipsGuideContent from './hooks/useTipsGuideContent';
import {getTipsCardConfig} from './tipsCardConfig';
import PopupPageView from './views/PopupPageView';

type TipsListViewProps = {
    onCardSelect: (card: TipsItem) => void;
};

const TipsListView = ({onCardSelect}: TipsListViewProps) => {
    const {title, description, cards, backgroundImage} = useTipsGuideContent();

    const handleBackClick = () => {
        eventMessagesService.sendBackButtonTriggerMessage();
    };

    return (
        <div className='relative flex h-full w-full flex-col overflow-y-auto overflow-x-hidden bg-tips-surface px-4 pt-safe-offset-4 pb-4 text-tips-text-primary'>
            <AemImage
                imageMedia={backgroundImage}
                className='pointer-events-none absolute top-0 left-0 z-0 h-full w-full'
                pictureClassName='w-full'
                imgClassName='h-auto w-full object-cover'
            />
            <div className='relative z-10'>
                <BackButton onClick={handleBackClick} className='mb-4' />
                <div className='mb-6'>
                    <h1 className='mt-0 mb-2 font-bold text-3xl'>{title}</h1>
                    <p className='max-w-xl text-base' dangerouslySetInnerHTML={{__html: description ?? ''}} />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                    {cards.map((card, index) => {
                        const {size, borderless} = getTipsCardConfig(card.type);

                        return (
                            <TipsCard
                                key={index}
                                title={card.title}
                                imageMedia={card.images}
                                size={size}
                                borderless={borderless}
                                className={clsx(size === TIPS_CARD_SIZES.BIG && 'col-span-2')}
                                onClick={() => {
                                    eventMessagesService.trackAmplitude(card.trackingValue);
                                    onCardSelect(card as TipsItem);
                                }}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const TipsAndTutorialsPage = () => {
    // Use stable refs for callbacks to avoid stale closures in navigate calls
    const onCardSelectRef = useRef<(card: TipsItem) => void>(null!);
    const onBackRef = useRef<() => void>(null!);

    const {navigate, ...transitionProps} = usePageTransition(
        useMemo(() => <TipsListView onCardSelect={(card) => onCardSelectRef.current(card)} />, [])
    );

    const handleBack = useCallback(() => {
        navigate(
            <TipsListView onCardSelect={(card) => onCardSelectRef.current(card)} />,
            NAVIGATION_DIRECTION.BACKWARD
        );
    }, [navigate]);

    const handleCardSelect = useCallback(
        (card: TipsItem) => {
            navigate(<PopupPageView card={card} onBack={() => onBackRef.current()} />, NAVIGATION_DIRECTION.FORWARD);
        },
        [navigate]
    );

    useInitialCardNavigation(handleCardSelect);

    // Keep refs up to date
    onCardSelectRef.current = (card) => handleCardSelect(card);
    onBackRef.current = handleBack;

    return <PageTransition navigate={navigate} {...transitionProps} />;
};

export default TipsAndTutorialsPage;
